import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import Navbar from "@/components/Navbar";

const POSTS = [
    {
        slug: "/blog/linkedin-posts-10x-engagement",
        title: "How to write LinkedIn posts that get 10x engagement (with AI)",
        excerpt: "Most LinkedIn posts get 5 likes from your college classmates. Here's the formula that changes that — and how to apply it in under 60 seconds.",
        tag: "LinkedIn Growth",
        readTime: "8 min",
        image: "/image/blog/linkedin-engagement.webp",
        defaultBg: "rgba(124,58,237,0.08)",
    },
    {
        slug: "/blog/indian-professionals-linkedin-personal-branding",
        title: "Why Indian professionals struggle with personal branding on LinkedIn",
        excerpt: "An honest look at the 5 real barriers that stop Indian professionals from posting — and how to break each one.",
        tag: "Personal Branding",
        readTime: "6 min",
        image: "/image/blog/personal-branding-india.webp",
        defaultBg: "rgba(6,182,212,0.08)",
    },
    {
        slug: "/blog/chatgpt-vs-lakshmi-linkedin-posts",
        title: "ChatGPT vs Lakshmi: which AI tool writes LinkedIn posts in your voice?",
        excerpt: "Both can write a LinkedIn post. Only one of them sounds like you, scores quality, and is built for Indian professionals.",
        tag: "Tool Comparison",
        readTime: "7 min",
        image: "/image/blog/chatgpt-vs-lakshmi.webp",
        defaultBg: "rgba(124,58,237,0.08)",
    },
];

export default function BlogIndex() {
    useEffect(() => {
        document.title = "Blog — LinkedIn Growth & Personal Branding Tips | Lakshmi";
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", "Practical guides for Indian professionals on writing LinkedIn posts, building personal brands, and using AI tools to grow on social media.");
    }, []);

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
                <div className="text-center mb-14">
                    <div className="lk-label mb-3">Resources & guides</div>
                    <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-4">
                        The <span className="lk-gradient-text">Lakshmi Blog</span>
                    </h1>
                    <p className="text-[var(--lk-muted)] text-base sm:text-lg max-w-xl mx-auto">
                        Practical guides for Indian professionals on LinkedIn growth, personal branding, and using AI to build your presence.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {POSTS.map((post, i) => (
                        <motion.div key={post.slug}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.08 }}
                            viewport={{ once: true }}
                        >
                            <Link to={post.slug} className="block lk-border lk-surface rounded-2xl overflow-hidden lk-card-hover h-full">
                                <div className="h-44 overflow-hidden" style={{ background: post.defaultBg }}>
                                    <img src={post.image} alt={post.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        onError={e => { e.target.style.display = "none"; }} />
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                                            style={{ background: "rgba(124,58,237,0.15)", color: "#9B5CF6", border: "1px solid rgba(124,58,237,0.3)" }}>
                                            {post.tag}
                                        </span>
                                        <span className="text-xs text-[var(--lk-muted)] flex items-center gap-1">
                                            <Clock className="w-3 h-3" />{post.readTime}
                                        </span>
                                    </div>
                                    <h2 className="font-semibold text-sm leading-snug mb-2">{post.title}</h2>
                                    <p className="text-xs text-[var(--lk-muted)] leading-relaxed">{post.excerpt}</p>
                                    <div className="mt-4 text-xs text-[var(--lk-primary)] flex items-center gap-1 font-medium">
                                        Read article <ArrowRight className="w-3 h-3" />
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-16 text-center lk-border lk-surface rounded-2xl p-8">
                    <h3 className="font-semibold text-lg mb-2">Ready to put this into practice?</h3>
                    <p className="text-[var(--lk-muted)] text-sm mb-5">Write your first AI LinkedIn post in under 60 seconds — free.</p>
                    <Link to="/signup" className="lk-btn-primary rounded-xl px-8 py-3.5 inline-flex items-center gap-2 text-sm">
                        Get started free <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
